import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { readSelectedFishGroupId } from '@/contexts/cageFilter/params';
import { useRouteInfo } from '@/hooks/useRouteInfo';

import { analytics } from '../core/analytics';
import { pageviewsAreHeld, subscribePageviewGate } from '../core/pageviewGate';
import { createPostHogAdapter } from '../posthog/postHogAdapter';
import { initializePostHogAnalytics } from '../posthog/posthogInit';

import { resolvePageContext } from './pageRegistry';
import { buildRouteContext, getRouteContextFromPathname } from './routeContext';
import { useSessionTracking } from './useSessionTracking';

interface AnalyticsProviderProps {
    children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
    const posthog = usePostHog();
    const { pathname, search } = useLocation();
    const { clientId, locationId, cageId } = useRouteInfo();
    const [pageviewEpoch, setPageviewEpoch] = useState(0);

    const pageContext = useMemo(() => resolvePageContext(pathname), [pathname]);
    const fishGroupId = readSelectedFishGroupId(search ?? '');

    const contextSnapshot = useMemo(
        () => buildRouteContext(pathname, clientId, locationId, cageId, pageContext, fishGroupId),
        [pathname, clientId, locationId, cageId, pageContext, fishGroupId]
    );

    const posthogAdapter = useMemo(
        () => (posthog ? createPostHogAdapter(posthog) : null),
        [posthog]
    );

    const previousPathRef = useRef<string | null>(null);
    const previousUrlRef = useRef<string | null>(null);

    useEffect(() => subscribePageviewGate(() => setPageviewEpoch((epoch) => epoch + 1)), []);

    useEffect(() => {
        analytics.setFallbackContextGetter(() =>
            getRouteContextFromPathname(window.location.pathname, window.location.search ?? '')
        );

        return () => {
            analytics.setFallbackContextGetter(null);
            analytics.setAdapter(null);
        };
    }, []);

    useEffect(() => {
        if (!posthog) {
            analytics.setAdapter(posthogAdapter);
            return;
        }

        let cancelled = false;
        let timer = 0;
        let attempts = 0;

        const start = (): void => {
            if (cancelled || attempts >= 20) {
                return;
            }

            if (initializePostHogAnalytics(posthog) === false) {
                attempts += 1;
                timer = window.setTimeout(start, 0);
            }
        };

        start();
        analytics.setAdapter(posthogAdapter);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [posthog, posthogAdapter]);

    // Must run before setRouteContext so $pageleave keeps the previous page's context.
    // pageviewEpoch is only a signal that auth released the gate; the effect reads the gate itself.
    // biome-ignore lint/correctness/useExhaustiveDependencies: epoch retriggers the effect when the gate opens
    useEffect(() => {
        if (pageviewsAreHeld()) {
            return;
        }

        if (previousPathRef.current === null || previousPathRef.current === pathname) {
            return;
        }

        if (previousUrlRef.current) {
            analytics.pageLeft({ currentUrl: previousUrlRef.current });
        }
    }, [pathname, pageviewEpoch]);

    useEffect(() => {
        analytics.setRouteContext(contextSnapshot);
    }, [contextSnapshot]);

    useSessionTracking();

    // biome-ignore lint/correctness/useExhaustiveDependencies: epoch retriggers the effect when the gate opens
    useEffect(() => {
        if (pageviewsAreHeld()) {
            return;
        }

        if (previousPathRef.current === pathname) {
            return;
        }

        previousPathRef.current = pathname;
        previousUrlRef.current = window.location.href;
        analytics.pageViewed({ currentUrl: window.location.href });
    }, [pathname, pageviewEpoch]);

    return <>{children}</>;
}
